import React, { useState } from "react";
import { Layers, Trophy, User, UsersRound } from "lucide-react";
import { __asStr } from "@tennis-matches/core/utils";
import {
  SimpleFlowFooter,
  SimpleFlowShell,
} from "@tennis-matches/ui-components";
import {
  PlayerStep,
  PeriodsStep,
  ClubRulesStep,
  GroupsKoStep,
  ModeSelectStep,
  KoOnlyStep,
} from "@tennis-matches/ui/tournament-setup";

const STEPS = [
  { id: 1, key: "day" },
  { id: 2, key: "players" },
  { id: 3, key: "flow" },
];

const FORMAT_CARDS = [
  {
    value: "singles",
    titleKey: "settings.format.singles",
    Icon: User,
  },
  {
    value: "fixedDoubles",
    titleKey: "settings.format.fixedDoubles",
    Icon: UsersRound,
  },
];

function formatLabel(t, format) {
  if (format === "fixedDoubles") return t("settings.format.fixedDoubles");
  return t("settings.format.singles");
}

function modeLabel(t, mode) {
  if (mode === "ko") return t("club.mode.ko.title");
  if (mode === "swiss") return t("club.mode.swiss.title");
  return t("club.mode.groupsKo.title");
}

/**
 * 3-Schritt ClubChamp-Setup: Turnier → Spieler → Ablauf.
 */
export function ClubChampSimpleFlow({
  t,
  flowStep = 1,
  onFlowStepChange,
  visitedSteps,
  tournamentName = "",
  onTournamentNameChange,
  format = "singles",
  onFormatChange,
  tournamentMode = "groupsKo",
  onTournamentModeChange,
  names = {},
  setNames,
  teamAllocationMode = "lkBased",
  setTeamAllocationMode,
  manualTeams = [],
  setManualTeams,
  lkByPlayer = null,
  setLkByPlayer,
  excludesByPlayer = null,
  setExcludesByPlayer,
  playerEntryMode = "manual",
  setPlayerEntryMode,
  registrationUrl = "",
  groupsKo = null,
  setGroupsKo,
  koOnly = null,
  setKoOnly,
  periods = null,
  setPeriods,
  clubRules = null,
  setClubRules,
  effectiveEntrantsCount = 0,
  playerCount = 0,
  minRequiredPlayers = 8,
  onSearchPlayers = null,
  onAddPlayer = null,
  onPreparePlayerContact = null,
  onCreatePlayerContact = null,
  onPlayerContactResolved = null,
  state = null,
  wizardMode = "simple",
  stepOk = null,
  validation = null,
  overviewHref = "/hub/clubmeisterschaft",
  primaryLabelKey = "club.simple.finish.prepare",
  onPrimary,
  primaryDisabled = false,
  primaryBusy = false,
  primaryHint = "",
  onDone = null,
  doneLabelKey = "club.simple.done",
  doneDisabled = false,
}) {
  const [summaryOpen, setSummaryOpen] = useState(false);

  const goStep = (id) => {
    const n = Math.max(1, Math.min(3, Number(id) || 1));
    onFlowStepChange?.(n);
  };

  const tryNext = () => {
    if (flowStep >= 3) return;
    goStep(flowStep + 1);
  };

  const clubRulesSafe =
    clubRules ??
    (format === "fixedDoubles"
      ? { scoring: "mtb", setEnd: "tb" }
      : { scoring: "bo3", setEnd: "tb" });

  const cardNav = (
    <SimpleFlowFooter
      flowStep={flowStep}
      maxStep={3}
      onBack={() => goStep(flowStep - 1)}
      onNext={tryNext}
      nextLabel={t("club.simple.next")}
      backLabel={t("settings.wizard.back")}
      onDone={onDone}
      doneLabel={t(doneLabelKey)}
      doneDisabled={doneDisabled}
      primaryBusy={primaryBusy}
      onPrimary={onPrimary}
      primaryLabel={primaryBusy ? t("common.loading") : t(primaryLabelKey)}
      primaryDisabled={primaryDisabled}
      primaryHint={primaryHint}
    />
  );

  const summaryCard = (
    <aside
      className="mm-simple__summary"
      aria-label={t("club.simple.summary.aria")}
    >
      <div className="mm-simple__summaryCard">
        <h2 className="mm-simple__summaryTitle">
          {t("club.simple.summary.title")}
        </h2>
        <div className="mm-simple__summaryBadges">
          <span className="mm-simple__badge">{formatLabel(t, format)}</span>
          <span className="mm-simple__badge mm-simple__badge--muted">
            {modeLabel(t, tournamentMode)}
          </span>
        </div>
        <div className="mm-simple__summaryStats">
          <div>
            <span className="mm-simple__summaryStatValue">{playerCount}</span>
            <span className="mm-simple__summaryStatLabel">
              {t("club.simple.summary.players")}
            </span>
          </div>
          <div>
            <span className="mm-simple__summaryStatValue" aria-hidden>
              {tournamentMode === "ko" ? (
                <Trophy size={22} />
              ) : (
                <Layers size={22} />
              )}
            </span>
            <span className="mm-simple__summaryStatLabel">
              {t("club.simple.summary.mode")}
            </span>
          </div>
        </div>
      </div>
    </aside>
  );

  return (
    <SimpleFlowShell
      flowStep={flowStep}
      steps={STEPS}
      stepLabel={(s) => t(`club.simple.steps.${s.key}`)}
      stepsAriaLabel={t("club.simple.steps.aria")}
      visitedSteps={visitedSteps}
      onStepChange={goStep}
      backLabel={t("club.simple.backToOverview")}
      overviewHref={overviewHref}
      onDone={onDone}
      doneDisabled={doneDisabled}
      primaryBusy={primaryBusy}
      eventTitle={__asStr(tournamentName) || t("club.simple.untitled")}
      eventMeta={modeLabel(t, tournamentMode)}
      dataApp="clubchamp"
      summary={
        <>
          <details
            className="mm-simple__summaryMobile"
            open={summaryOpen}
            onToggle={(e) => setSummaryOpen(e.currentTarget.open)}
          >
            <summary>{t("club.simple.summary.title")}</summary>
            {summaryCard}
          </details>
          <div className="mm-simple__summaryDesktop">{summaryCard}</div>
        </>
      }
    >
      {flowStep === 1 ? (
        <section
          className="mm-simple__panel"
          aria-labelledby="cc-simple-day-title"
        >
          <div className="mm-simple__panelBody">
            <h2 id="cc-simple-day-title" className="mm-simple__heading">
              {t("club.simple.day.title")}
            </h2>

            <label className="mm-simple__field">
              <span className="mm-simple__fieldLabel">
                {t("club.simple.day.name")}
              </span>
              <input
                type="text"
                className="mm-simple__input"
                value={tournamentName}
                onChange={(e) => onTournamentNameChange?.(e.target.value)}
                placeholder={t("club.simple.untitled")}
              />
            </label>

            <p className="mm-simple__sectionLabel">
              {t("club.simple.day.format")}
            </p>
            <div
              className="mm-simple__choiceGrid"
              role="radiogroup"
              aria-label={t("club.simple.day.formatAria")}
            >
              {FORMAT_CARDS.map((c) => {
                const Icon = c.Icon;
                const selected = format === c.value;
                return (
                  <button
                    key={c.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`mm-simple__choice${selected ? " is-selected" : ""}`}
                    onClick={() => onFormatChange?.(c.value)}
                  >
                    <Icon size={22} aria-hidden />
                    <span>{t(c.titleKey)}</span>
                  </button>
                );
              })}
            </div>

            <p className="mm-simple__sectionLabel">
              {t("club.simple.day.mode")}
            </p>
            <ModeSelectStep
              t={t}
              value={tournamentMode}
              onChange={onTournamentModeChange}
            />
          </div>
          {cardNav}
        </section>
      ) : null}

      {flowStep === 2 ? (
        <section
          className="mm-simple__panel"
          aria-labelledby="cc-simple-players-title"
        >
          <div className="mm-simple__panelBody">
            <h2 id="cc-simple-players-title" className="mm-simple__heading">
              {t("club.simple.steps.players")}
            </h2>
            <div className="formgrid tm-wizard-v0__form">
              <PlayerStep
                state={state}
                wizardMode={wizardMode}
                format={format}
                names={names}
                setNames={setNames}
                teamAllocationMode={teamAllocationMode}
                setTeamAllocationMode={setTeamAllocationMode}
                manualTeams={manualTeams}
                setManualTeams={setManualTeams}
                lkByPlayer={lkByPlayer}
                setLkByPlayer={setLkByPlayer}
                excludesByPlayer={excludesByPlayer}
                setExcludesByPlayer={setExcludesByPlayer}
                t={t}
                onSearchPlayers={onSearchPlayers}
                onAddPlayer={onAddPlayer}
                onPreparePlayerContact={onPreparePlayerContact}
                onCreatePlayerContact={onCreatePlayerContact}
                onPlayerContactResolved={onPlayerContactResolved}
                onDeletePlayer={null}
                playerEntryMode={playerEntryMode}
                setPlayerEntryMode={setPlayerEntryMode}
                registrationUrl={registrationUrl}
                showLkOnSingles
                minRequiredOverride={minRequiredPlayers}
                minFieldsOverride={minRequiredPlayers}
              />
            </div>
          </div>
          {cardNav}
        </section>
      ) : null}

      {flowStep === 3 ? (
        <section
          className="mm-simple__panel"
          aria-labelledby="cc-simple-flow-title"
        >
          <div className="mm-simple__panelBody">
            <h2 id="cc-simple-flow-title" className="mm-simple__heading">
              {t("club.simple.flow.title")}
            </h2>
            <p className="mm-simple__hint">{t("club.simple.flow.hint")}</p>

            <div className="formgrid">
              {tournamentMode === "groupsKo" ? (
                <GroupsKoStep
                  t={t}
                  value={groupsKo}
                  onChange={setGroupsKo}
                  entrantsCount={effectiveEntrantsCount}
                  suggest={true}
                  validation={validation}
                  stepOk={stepOk}
                  names={names}
                  lkByPlayer={lkByPlayer}
                />
              ) : tournamentMode === "ko" ? (
                <KoOnlyStep
                  t={t}
                  value={koOnly}
                  onChange={setKoOnly}
                  entrantsCount={effectiveEntrantsCount}
                  names={names}
                  lkByPlayer={lkByPlayer}
                />
              ) : (
                <div className="wizardStep" data-placeholder="swiss">
                  <p className="muted">{t("club.mode.swiss.placeholder")}</p>
                </div>
              )}
            </div>

            <div className="formgrid">
              <PeriodsStep
                t={t}
                mode={tournamentMode || "groupsKo"}
                periods={periods}
                setPeriods={setPeriods}
              />
            </div>

            <div className="formgrid">
              <ClubRulesStep
                t={t}
                value={clubRulesSafe}
                onChange={setClubRules ?? (() => {})}
                format={format}
              />
            </div>
          </div>
          {cardNav}
        </section>
      ) : null}
    </SimpleFlowShell>
  );
}
